import React, { useMemo, useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Badge,
  IconButton,
} from "@mui/material";
import ButtonComponent from "../button/Button";
import CloseIcon from "@mui/icons-material/Close";
type BulkPasteFilterProps = {
  gridRef: any;
  columns: any[];
  triggerClearFilter: boolean;setIsTriggerFilter: React.Dispatch<React.SetStateAction<boolean>>
};

const BulkPasteFilter = ({
  gridRef,
  columns,
  setIsTriggerFilter,
  triggerClearFilter = false,
}: BulkPasteFilterProps) => {
  const [open, setOpen] = useState(false);
  const [selectedColumn, setSelectedColumn] = useState("");
  const [pasteText, setPasteText] = useState("");
  const [filterApplied, setFilterApplied] = useState(false);
  const filterColumns = useMemo(() => {
    const result: { label: string; value: string }[] = [];

    const walk = (cols: any[]) => {
      cols?.forEach((col) => {
        if (col.children?.length) {
          walk(col.children);
        } else if (col.field) {
          result.push({
            label: col.headerName ?? col.field,
            value: col.field,
          });
        }
      });
    };

    walk(columns || []);

    return result.sort((a, b) => a.label.localeCompare(b.label));
  }, [columns]);

  const applyFilter = () => {
    const api = gridRef?.current?.api;

    if (!api || !selectedColumn) return;

    const values = [
      ...new Set(
        pasteText
          .split(/[\n\r\t,]+/)
          .map((v) => v.trim())
          .filter(Boolean),
      ),
    ];

    const filterValues = new Set(values);

    api.setFilterModel(null);

    api.setGridOption("isExternalFilterPresent", () => filterValues.size > 0);

    api.setGridOption("doesExternalFilterPass", (node: any) => {
      const cellValue = String(node.data?.[selectedColumn] ?? "").trim();

      return filterValues.has(cellValue);
    });

    api.onFilterChanged();
    setFilterApplied(true);
    setOpen(false);
  };
  useEffect(() => {
    if (!triggerClearFilter) return;
    clearFilter();
    setIsTriggerFilter(false);
  }, [triggerClearFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  const clearFilter = () => {
    const api = gridRef?.current?.api;

    if (api) {
      api.setGridOption("isExternalFilterPresent", () => false);

      api.setGridOption("doesExternalFilterPass", () => true);

      api.onFilterChanged();
    }
    setFilterApplied(false);
    setSelectedColumn("");
    setPasteText("");
  };

  return (
    <>
      <Badge
        color="error"
        variant="dot"
        invisible={!filterApplied}
        overlap="circular"
      >
        <ButtonComponent
          className="take-action"
          type="secondary"
          label="Bulk Filter"
          onClick={() => setOpen(true)}
          width="auto"
          maxWidth="none"
          styles={{ borderRadius: 28 }}
        />
      </Badge>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ position: "relative", pr: 6 }}>
          Bulk Paste Filter
          <IconButton
            aria-label="close"
            onClick={() => setOpen(false)}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  { borderColor: "#000" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
              }}
            >
              <InputLabel>Column</InputLabel>

              <Select
                value={selectedColumn}
                label="Column"
                onChange={(e) => setSelectedColumn(e.target.value as string)}
              >
                {filterColumns.map((col) => (
                  <MenuItem key={col.value} value={col.value}>
                    {col.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              multiline
              rows={8}
              fullWidth
              sx={{
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  { borderColor: "#000" },
                "& .MuiInputLabel-root.Mui-focused": { color: "#000" },
              }}
              label="Paste Values"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
              placeholder={`100001
100002
100003

or

100001,100002,100003`}
            />
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={clearFilter} color="inherit">
            Clear
          </Button>
          <ButtonComponent
            className="take-action"
            type="secondary"
            label="Apply"
            onClick={applyFilter}
            disabled={!selectedColumn || !pasteText.trim()}
            width="auto"
            maxWidth="none"
            styles={{ borderRadius: 28 }}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default BulkPasteFilter;
